import React from 'react';
import { T } from './constants';
import { Wave, BrandMark, OpenInAppPill } from './components';
import { fmt } from './utils';
import { SharedAudioDetail } from './types';

export default function AudioDetailPage({
  data,
  onPlay,
}: {
  data: SharedAudioDetail;
  onPlay: (startTime?: number) => void;
}) {
  return (
    <div
      data-screen-label="01 Audio Detail"
      style={{
        position: 'absolute',
        inset: 0,
        overflowY: 'auto',
        background: T.background,
        color: T.text,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Top bar */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)',
          paddingBottom: 12,
          paddingLeft: 18,
          paddingRight: 12,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: T.background,
        }}
      >
        <BrandMark />
        <OpenInAppPill compact videoId={data.video_id} />
      </div>

      <div style={{ flex: 1, padding: '4px 20px 24px' }}>
        {/* Hero — cover + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '12px 0 28px' }}>
          <div
            style={{
              width: 96,
              height: 96,
              borderRadius: 18,
              flexShrink: 0,
              background: `linear-gradient(135deg, ${T.primary}, ${T.primaryLight})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 24px rgba(255,145,77,0.28)',
            }}
          >
            <Wave color="rgba(255,255,255,0.92)" size={52} />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                font: '700 19px/1.3 "Cal Sans","Inter",system-ui',
                color: T.text,
                textWrap: 'balance',
              }}
            >
              {data.title}
            </div>
            <div style={{ marginTop: 8, color: T.textSecondary, fontSize: 13 }}>
              {Math.round(data.duration / 60)} min
            </div>
          </div>
        </div>

        {/* Show Notes */}
        <div style={{ marginBottom: 32 }}>
          <div
            style={{
              font: '600 18px/1.2 "Cal Sans","Inter",system-ui',
              color: T.text,
              marginBottom: 12,
            }}
          >
            Show Notes
          </div>
          <div
            style={{
              padding: 16,
              borderRadius: 16,
              background: T.surface,
              border: `1px solid ${T.border}`,
              font: '400 14px/1.65 "Inter",system-ui',
              color: T.text,
            }}
          >
            {data.subject}
          </div>
        </div>

        {/* Timeline */}
        <div>
          <div
            style={{
              font: '600 18px/1.2 "Cal Sans","Inter",system-ui',
              color: T.text,
              marginBottom: 16,
            }}
          >
            Timeline
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {data.timeline.map((it, i) => {
              const isLast = i === data.timeline.length - 1;
              return (
                <div key={i} style={{ display: 'flex', gap: 14, paddingBottom: isLast ? 0 : 14 }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
                    <div
                      style={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        background: i === 0 ? T.primary : 'rgba(138,130,122,0.4)',
                      }}
                    />
                    {!isLast && (
                      <div
                        style={{
                          width: 2,
                          flex: 1,
                          marginTop: 4,
                          background: 'rgba(138,130,122,0.25)',
                        }}
                      />
                    )}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        font: '500 11px/1 "Inter",system-ui',
                        color: T.primary,
                        marginBottom: 4,
                        letterSpacing: 0.2,
                      }}
                    >
                      {fmt(it.start_time)} - {fmt(it.end_time)}
                    </div>
                    <div
                      style={{
                        font: '500 15px/1.3 "Inter",system-ui',
                        color: T.text,
                        marginBottom: 2,
                      }}
                    >
                      {it.title}
                    </div>
                    <div
                      style={{
                        font: '400 13px/1.4 "Inter",system-ui',
                        color: T.textSecondary,
                      }}
                    >
                      {it.subtitle}
                    </div>
                  </div>
                  <button
                    onClick={() => onPlay(it.start_time)}
                    aria-label="Play from here"
                    style={{
                      width: 32,
                      height: 32,
                      padding: 0,
                      border: 'none',
                      cursor: 'pointer',
                      background: 'transparent',
                      alignSelf: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <svg width="22" height="22" viewBox="0 0 22 22">
                      <circle cx="11" cy="11" r="10" fill={T.primary} />
                      <path d="M9 7v8l6-4-6-4z" fill="#fff" />
                    </svg>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Sticky Play CTA */}
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          left: 0,
          right: 0,
          marginTop: 'auto',
          padding: '24px 20px calc(env(safe-area-inset-bottom, 0px) + 18px)',
          background:
            'linear-gradient(180deg, rgba(253,249,243,0) 0%, rgba(253,249,243,0.96) 35%, #FDF9F3 65%)',
          pointerEvents: 'none',
        }}
      >
        <button
          onClick={() => onPlay(0)}
          style={{
            width: '100%',
            height: 56,
            borderRadius: 18,
            border: 'none',
            background: '#000',
            color: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 10,
            font: '600 17px/1 "Cal Sans","Inter",system-ui',
            boxShadow: '0 8px 22px rgba(26,24,21,0.30)',
            pointerEvents: 'auto',
          }}
        >
          <svg width="14" height="14" viewBox="0 0 14 14" fill="#fff">
            <path d="M3 1v12l9-6-9-6z" />
          </svg>
          Play Episode
        </button>
      </div>
    </div>
  );
}
